// "Send my accountant a screenshot" — one frame of a window, tab or screen
// the person picks, as a PNG.
//
// THE STREAM IS STOPPED THE MOMENT THE FRAME IS DRAWN. The browser shows a
// "sharing your screen" bar for as long as a track is live, and a bar that
// stays up after the picture is taken reads as the app watching. Stopped in a
// `finally`, so a failed draw stops it too.
//
// MOST MOBILE BROWSERS HAVE NO getDisplayMedia, so `canCaptureScreen` is what
// decides whether the button exists at all. Paste and the file picker work
// everywhere and stay.

/** The longest edge kept. A 5K display's full-size PNG is often over the cap. */
export const MAX_EDGE = 3840;

export const canCaptureScreen = (): boolean =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getDisplayMedia === 'function';

/** Thrown when the person closes the picker: not an error worth showing. */
export class CaptureCancelled extends Error {
  constructor() {
    super('Screen capture was cancelled');
    this.name = 'CaptureCancelled';
  }
}

function stamp(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-` +
    `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

export function screenshotName(now: Date = new Date(), extension = 'png'): string {
  return `screenshot-${stamp(now)}.${extension}`;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Could not draw the screenshot'))),
                  type, quality);
  });
}

/** Waits until the video has a frame to draw, not merely dimensions. */
async function firstFrame(video: HTMLVideoElement): Promise<void> {
  if (video.readyState < 1) {
    await new Promise<void>(resolve => { video.onloadedmetadata = () => resolve(); });
  }
  await video.play();
  const withFrameCallback = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: () => void) => number;
  };
  if (typeof withFrameCallback.requestVideoFrameCallback === 'function') {
    await new Promise<void>(resolve => withFrameCallback.requestVideoFrameCallback!(() => resolve()));
  } else {
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
  }
}

/**
 * Asks the person to pick what to share, takes one frame and stops sharing.
 * `maxBytes` is the server's per-file cap: a frame over it is sent as a JPEG
 * instead, which is still readable and a fraction of the size.
 */
export async function captureScreenshot(maxBytes: number): Promise<File> {
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
  } catch (e) {
    if (e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'AbortError')) {
      throw new CaptureCancelled();
    }
    throw e;
  }

  const canvas = document.createElement('canvas');
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await firstFrame(video);

    const scale = Math.min(1, MAX_EDGE / Math.max(video.videoWidth, video.videoHeight, 1));
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not draw the screenshot');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    video.srcObject = null;
  } finally {
    stream.getTracks().forEach(track => track.stop());
  }

  const now = new Date();
  const png = await toBlob(canvas, 'image/png');
  if (png.size <= maxBytes) return new File([png], screenshotName(now), { type: 'image/png' });

  const jpeg = await toBlob(canvas, 'image/jpeg', 0.85);
  return new File([jpeg], screenshotName(now, 'jpg'), { type: 'image/jpeg' });
}
